import React, { memo } from 'react';
import { RowRenderer, type RowChrome } from './RowRenderer';
import { rowHitShell } from './row-hit-shell';
import {
  padStyle,
  presentationTableProps,
  surfaceBorderTableStyle,
  surfaceGroupContentClipStyle,
} from './table';
import { emailRowAreaWidth, rowInnerWidth } from '../domain/column-presets';
import type { FacilityProfile } from '../domain/facility';
import { ZERO_PADDING, ZERO_RADIUS } from '../domain/create-shared';
import {
  surfaceBackgroundImageStyle,
} from '../domain/image-overlay';
import { shouldRenderRow } from '../domain/row-policy';
import type { Group, Padding } from '../domain/types';
import type { RenderTheme } from '../domain/text-theme';
import type { RenderMergeMode } from './BlockRenderer';

type GroupRendererProps = {
  group: Group;
  contentWidth: number;
  pagePadding?: Padding;
  facility: FacilityProfile;
  textTheme: RenderTheme;
  mergeMode: RenderMergeMode;
  chrome?: RowChrome;
  editorMode?: boolean;
};

export const GroupRenderer = memo(({
  group,
  contentWidth,
  pagePadding = ZERO_PADDING,
  facility,
  textTheme,
  mergeMode,
  chrome,
  editorMode = false,
}: GroupRendererProps) => {
  const useGroupHitShell = Boolean(chrome?.wrapGroup);
  const groupContentWidth = useGroupHitShell
    ? emailRowAreaWidth(contentWidth, pagePadding)
    : contentWidth;
  const groupInnerWidth = rowInnerWidth(
    groupContentWidth,
    group.padding,
    group.border,
  );

  const groupRadius = group.borderRadius ?? ZERO_RADIUS;
  const groupBorderTableStyle = surfaceBorderTableStyle(group.border, groupRadius);
  const groupContentCellStyle = {
    ...padStyle(group.padding),
    backgroundColor:
      group.backgroundColor === 'transparent'
        ? undefined
        : group.backgroundColor,
    ...surfaceBackgroundImageStyle(
      group.backgroundImage || undefined,
      group.backgroundImageOverlay,
    ),
    ...surfaceGroupContentClipStyle(groupRadius),
  };

  const tableProps = presentationTableProps();
  const innerTable = (
    <table
      {...tableProps}
      className={editorMode ? 'email-rail-anchor' : undefined}
      width="100%"
      style={{
        ...tableProps.style,
        width: editorMode ? groupContentWidth : '100%',
        maxWidth: groupContentWidth,
      }}>
      <tbody>
        <tr>
          <td style={{ padding: 0, verticalAlign: 'top' }}>
            <table
              {...tableProps}
              width="100%"
              style={{
                ...tableProps.style,
                ...groupBorderTableStyle,
              }}>
              <tbody>
                <tr>
                  <td style={groupContentCellStyle}>
                    {group.rows.length === 0 && chrome?.wrapEmptyGroupRows
                      ? chrome.wrapEmptyGroupRows(group)
                      : group.rows.reduce<React.ReactNode[]>((nodes, row, rowIndex) => {
                          if (!shouldRenderRow(row, editorMode)) {
                            return nodes;
                          }
                          nodes.push(
                            <RowRenderer
                              key={row.id}
                              row={row}
                              contentWidth={groupInnerWidth}
                              pagePadding={ZERO_PADDING}
                              facility={facility}
                              textTheme={textTheme}
                              mergeMode={mergeMode}
                              chrome={chrome}
                              editorMode={editorMode}
                              nested
                              group={group}
                              groupRowIndex={rowIndex}
                            />,
                          );
                          return nodes;
                        }, [])}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  if (!chrome?.wrapGroup) {
    return innerTable;
  }

  return <>{chrome.wrapGroup(group, rowHitShell(innerTable))}</>;
});
GroupRenderer.displayName = 'GroupRenderer';
